import React, { useEffect, useMemo, useState } from 'react';
import { RouteComponentProps } from '@reach/router';
import { v4 } from 'uuid';
import { FlexBehavior, FlexJustify, Spinner } from 'react-magma-dom';
import { Filter } from '../../components/Filter';
import { RelatedItem } from '../../components/RelatedElement';
import { ComingSoon } from '../../components/ComingSoon';
import { RELATED_MARGIN } from '../../components/RelatedElement/RelatedWrapperComponent';
import { ContactUsError } from '../../components/ContactUsError';
import {
  StyledCenteredSpinnerWrapper,
  MarginWrapper,
  RelatedWrapper,
  StyledContentWrapper as RelatedItemsWrapper,
} from '../../components/Styled';
import { profLearningApi } from '../../API/proffesionalLearning';
import { stagesApi } from '../../API/stages';
import { computeStageFilterItems, getAccessibleStages } from '../../utils/filter';
import { useQuery } from '../../hooks/useQuery';
import { ABOUT, NOT_FOUND, PROF_LEARNING_CATEGORIES, RELATED_ITEM_WIDTH, SKILLS } from '../../constants';
import { IAsset } from '../../constants/assets';
import { StageFilter } from '../../types/filter';
import { getStageColors } from '../../utils';

interface IRoute extends RouteComponentProps {
  stage: string;
}

export const ProfessionalLearning = (props: IRoute): JSX.Element => {
  const { stage, navigate } = props;

  const [assets, setAssets] = useState<IAsset[] | undefined>();
  const [filteredAssets, setFilteredAssets] = useState<IAsset[] | undefined>([]);
  const [selectedFilter, setSelectedFilter] = useState<StageFilter>(StageFilter.all);
  const [accessibleStages, setAccessibleStages] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isComingSoon, setIsComingSoon] = useState(false);
  const [error, setError] = useState(false);
  const filterItems = useMemo(() => computeStageFilterItems(accessibleStages), [accessibleStages]);
  const defaultFilter = useQuery('filter');

  useEffect(() => {
    if (!PROF_LEARNING_CATEGORIES.includes(stage)) {
      navigate && navigate(`/${NOT_FOUND}`);
    }

    (async () => {
      setAssets(undefined);
      setFilteredAssets([]);
      setIsLoading(true);

      try {
        const {
          data: { data },
        } = await profLearningApi.getProfLearning(stage);
        setAssets(data?.assets);
        setIsComingSoon(!data?.assets);
      } catch (error) {
        setError(!!error);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [stage]);

  useEffect(() => {
    (async () => {
      setIsLoading(true);

      try {
        const {
          data: { data },
        } = await stagesApi.getStages();
        setAccessibleStages(getAccessibleStages(data));
      } catch (error) {
        setError(!!error);
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (assets) {
      setFilteredAssets([]);
      onChangeFilter((defaultFilter ?? StageFilter.all) as StageFilter);
    }
  }, [assets]);

  const onChangeFilter = (filterState: StageFilter) => {
    if (!Object.values(StageFilter).includes(filterState)) {
      filterState = StageFilter.all;
    }

    setSelectedFilter(filterState);
    setFilteredAssets(
      filterState === StageFilter.all ? assets : assets?.filter(({ stages }) => stages.includes(filterState))
    );
  };

  if (error) {
    return <ContactUsError />;
  }

  if (isLoading) {
    const { main } = getStageColors(stage);

    return (
      <StyledCenteredSpinnerWrapper
        behavior={FlexBehavior.container}
        justify={FlexJustify.center}
        data-testid="spinner"
      >
        <Spinner color={main} size={50} />
      </StyledCenteredSpinnerWrapper>
    );
  }

  if (isComingSoon) {
    return <ComingSoon />;
  }

  return (
    <RelatedWrapper>
      <RelatedItemsWrapper width={RELATED_ITEM_WIDTH}>
        {[ABOUT, SKILLS].includes(stage) && (
          <Filter items={filterItems} selectedItem={selectedFilter} onChange={onChangeFilter} />
        )}
        {filteredAssets &&
          filteredAssets.map((asset: IAsset, index: number) => {
            return <RelatedItem key={v4()} asset={asset} index={index} />;
          })}
      </RelatedItemsWrapper>
      <MarginWrapper margin={RELATED_MARGIN} />
    </RelatedWrapper>
  );
};
